// Session 33: what came in from the sign-up page. Agency only.
//
// Each request shows the basket, what it comes to, and — the useful part —
// which entitlement keys the plan implies, so they can be switched on through
// the client's own settings page. This page deliberately does not write
// entitlements itself: portal_update_client is the one writer for those and it
// carries rules a second writer would drift from.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { fmtDateTime } from '../components/FormBits.jsx';

const money = (cents) => '$' + (cents / 100).toFixed(cents % 100 ? 2 : 0);

// Stripe's vocabulary, kept verbatim in the database so a status we have not
// thought about cannot silently read as "active". Translated only here, for
// display, with anything unrecognised shown as-is rather than hidden.
const SUB_STATUS = {
  trialing: ['In their free trial', 'chip-run-wait'],
  active: ['Paying', 'course-chip'],
  past_due: ['Payment failed — Stripe is retrying', 'chip-run-wait'],
  unpaid: ['Unpaid, Stripe has given up retrying', 'chip-run-wait'],
  incomplete: ['Never finished paying', 'chip-run-wait'],
  incomplete_expired: ['Never finished paying, expired', 'chip-run-wait'],
  canceled: ['Cancelled', 'chip-run-wait'],
  paused: ['Paused', 'chip-run-wait'],
};

const STATUS = [
  ['new', 'New'],
  ['contacted', 'Contacted'],
  ['converted', 'Signed up'],
  ['declined', 'Not going ahead'],
];
const STATUS_LABEL = Object.fromEntries(STATUS);

// The names the client settings page uses for the same things, so this page
// says "switch on GMB Audit" in the words of the switch she has to flip.
const ENTITLEMENT_LABEL = {
  seo_blogs: 'SEO Blogs',
  analytics: 'Analytics',
  gmb_audit: 'GMB Audit',
  seo_recommendations: 'SEO recommendations',
  seo_recommendations_preview: 'SEO recommendations (Preview)',
  ai_courses: 'AI courses',
  ai_courses_preview: 'AI courses (Preview)',
};

export default function AdminSignups() {
  const { isAgency, clients } = useAuth();
  const [rows, setRows] = useState(null);
  const [subs, setSubs] = useState([]);
  const [newAccounts, setNewAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(null);
  const [banner, setBanner] = useState(null);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    const [reqs, subs, fresh] = await Promise.all([
      // The view orders already, but order through a view is not a promise
      // Postgres makes, and this list is read newest-first on purpose.
      supabase.from('v_portal_signup_requests').select('*')
        .order('created_at', { ascending: false }),
      supabase.from('v_portal_subscriptions').select('*')
        .order('created_at', { ascending: false }),
      supabase.from('v_portal_new_accounts').select('*')
        .order('created_at', { ascending: false }),
    ]);
    const bad = reqs.error || subs.error || fresh.error;
    if (bad) { setError(bad.message); return; }
    setRows(reqs.data || []);
    setSubs(subs.data || []);
    setNewAccounts(fresh.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!isAgency) return <div className="empty"><h2>Not available</h2></div>;
  if (error) return <div className="empty"><h2>Couldn't load sign-ups</h2><p>{error}</p></div>;
  if (!rows) return <p className="lede">Loading…</p>;

  const shown = showAll ? rows : rows.filter((r) => r.status === 'new');
  const openCount = rows.filter((r) => r.status === 'new').length;

  async function linkToClient(subId, clientId) {
    if (!clientId) return;
    setBusy(subId); setBanner(null);
    const { error: e } = await supabase.rpc('portal_link_subscription', {
      p_subscription_id: subId, p_client_id: clientId,
    });
    setBusy(null);
    if (e) { setBanner({ tone: 'err', text: e.message }); return; }
    setBanner({ tone: 'ok', text: 'Linked.' });
    await load();
  }

  async function setStatus(id, status) {
    setBusy(id); setBanner(null);
    const { error: e } = await supabase.rpc('portal_set_signup_status', {
      p_id: id, p_status: status, p_note: notes[id] || null,
    });
    setBusy(null);
    if (e) { setBanner({ tone: 'err', text: e.message }); return; }
    setBanner({ tone: 'ok', text: `Marked ${STATUS_LABEL[status].toLowerCase()}.` });
    setNotes((n) => ({ ...n, [id]: '' }));
    await load();
  }

  return (
    <>
      <h1>Sign-ups</h1>
      <p className="lede">
        {newAccounts.length > 0 && (
          <><strong>
            {newAccounts.length} new {newAccounts.length === 1 ? 'account' : 'accounts'} being
            set up.
          </strong>{' '}</>
        )}
        {openCount === 0 ? 'No unanswered requests.'
          : `${openCount} ${openCount === 1 ? 'request' : 'requests'} waiting on you.`}
      </p>

      {banner && <div className={`banner banner-${banner.tone === 'ok' ? 'ok' : 'err'}`}>{banner.text}</div>}

      {newAccounts.length > 0 && (
        <>
          <h2 style={{ marginTop: 26 }}>New accounts being set up</h2>
          <p className="hint">
            Paying, and not yet switched on. Nothing is drafted, scheduled or
            published for a client until you move them to active, so nobody here
            is getting work yet.
          </p>
          {newAccounts.map((a) => {
            // "Ready for you" is the one that needs doing; the rest are the
            // customer's move, or a waiting invite.
            const ready = !!a.onboarding_completed_at;
            const stuck = a.users === 0 || !a.last_sign_in_at;
            return (
              <section key={a.client_id}
                       className={`review-section signup-row${ready ? ' onb-section-done' : ''}`}>
                <div className="signup-row-head">
                  <h2>
                    {a.name}
                    <span className={`chip ${ready ? 'course-chip' : 'chip-run-wait'}`}>
                      {a.waiting_on}
                    </span>
                  </h2>
                  <span className="hint">{fmtDateTime(a.created_at)}</span>
                </div>
                <dl className="signup-row-facts">
                  <dt>Paying for</dt>
                  <dd>
                    {a.plan_names || '—'}
                    {a.amount_cents != null && (
                      <> <strong>{money(a.amount_cents)}{' '}
                        {a.bill_interval === 'annual' ? 'a year' : 'a month'}</strong></>
                    )}
                    {a.subscription_status === 'trialing' && a.trial_end && (
                      <span className="hint" style={{ display: 'block', margin: '4px 0 0' }}>
                        Free until {fmtDateTime(a.trial_end)}.
                      </span>
                    )}
                  </dd>
                  <dt>Setup form</dt>
                  <dd>
                    {(a.sections_done || []).length === 0
                      ? 'Nothing filled in yet'
                      : `Saved: ${(a.sections_done || []).join(', ')}`}
                    {a.onboarding_completed_at && (
                      <> — <strong>finished {fmtDateTime(a.onboarding_completed_at)}</strong></>
                    )}
                  </dd>
                  <dt>Signed in</dt>
                  <dd>
                    {a.last_sign_in_at ? fmtDateTime(a.last_sign_in_at)
                      : (a.users === 0
                          ? 'No portal user yet — the invite job has not run'
                          : 'Never. Their invite may be sitting unopened.')}
                  </dd>
                  <dt>Switch on</dt>
                  <dd>
                    {(a.entitlements || []).map((k) => ENTITLEMENT_LABEL[k] || k).join(', ') || '—'}
                    <span className="hint" style={{ display: 'block', margin: '4px 0 0' }}>
                      Already granted. Moving them to active is what starts the
                      pipeline, and that is on their settings page.
                    </span>
                  </dd>
                </dl>
                <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
                  <Link className="btn" to={`/admin/clients/${a.client_id}/settings?client=${a.client_id}`}>
                    {ready ? 'Set them up' : 'Open their settings'}
                  </Link>
                  {stuck && (
                    <span className="hint" style={{ margin: 0 }}>
                      If their invite never arrived, re-send it from the droplet with
                      {' '}<code>lotus_client_add.py --client-id {a.client_id} --invite-email …</code>
                    </span>
                  )}
                </div>
              </section>
            );
          })}
        </>
      )}

      {subs.length > 0 && (
        <>
          <h2 style={{ marginTop: 26 }}>Subscriptions</h2>
          <p className="hint">
            Straight from Stripe. Stripe decides what someone pays and whether
            they are still subscribed; this is what it told us.
          </p>
          {subs.map((sub) => {
            const [label, chip] = SUB_STATUS[sub.status] || [sub.status, 'chip-run-wait'];
            return (
              <section key={sub.stripe_subscription_id} className="review-section signup-row">
                <div className="signup-row-head">
                  <h2>
                    {sub.client_name || sub.email}
                    <span className={`chip ${chip}`}>{label}</span>
                    {sub.cancel_at_period_end && (
                      <span className="chip chip-run-wait">cancelling at the period end</span>
                    )}
                  </h2>
                  <span className="hint">{fmtDateTime(sub.created_at)}</span>
                </div>
                <dl className="signup-row-facts">
                  <dt>Email</dt><dd><a href={`mailto:${sub.email}`}>{sub.email}</a></dd>
                  <dt>Paying for</dt>
                  <dd>
                    {(sub.plan_names || []).join(', ') || '—'}
                    {sub.amount_cents != null && (
                      <>
                        {' '}<strong>
                          {money(sub.amount_cents)}{' '}
                          {sub.bill_interval === 'annual' ? 'a year' : 'a month'}
                        </strong>
                      </>
                    )}
                  </dd>
                  {sub.in_trial && (
                    <>
                      <dt>Free until</dt>
                      <dd>{fmtDateTime(sub.trial_end)} — nothing charged before then</dd>
                    </>
                  )}
                  {sub.current_period_end && (
                    <>
                      <dt>{sub.cancel_at_period_end ? 'Access until' : 'Next charge'}</dt>
                      <dd>{fmtDateTime(sub.current_period_end)}</dd>
                    </>
                  )}
                  <dt>Switch on</dt>
                  <dd>
                    {(sub.implies_entitlements || []).length === 0 ? '—'
                      : (sub.implies_entitlements || [])
                          .map((k) => ENTITLEMENT_LABEL[k] || k).join(', ')}
                    <span className="hint" style={{ display: 'block', margin: '4px 0 0' }}>
                      Set these on the client's settings page. Paying does not switch
                      anything on by itself, on purpose.
                    </span>
                  </dd>
                  <dt>Client</dt>
                  <dd>
                    {sub.client_id ? (
                      <Link to={`/admin/clients/${sub.client_id}/settings?client=${sub.client_id}`}>
                        {sub.client_name || sub.client_id}
                      </Link>
                    ) : (
                      <>
                        <select className="course-input" defaultValue=""
                                disabled={busy === sub.stripe_subscription_id}
                                onChange={(e) => linkToClient(sub.stripe_subscription_id, e.target.value)}>
                          <option value="">Not linked — choose a client…</option>
                          {(clients || []).map((c) => (
                            <option key={c.client_id} value={c.client_id}>{c.name}</option>
                          ))}
                        </select>
                        <span className="hint" style={{ display: 'block', margin: '4px 0 0' }}>
                          Someone who has paid is not a client yet. Create the client
                          first, then link them here.
                        </span>
                      </>
                    )}
                  </dd>
                </dl>
              </section>
            );
          })}
          <h2 style={{ marginTop: 26 }}>Requests</h2>
        </>
      )}

      <div className="review-actions" style={{ justifyContent: 'flex-start', marginTop: 0 }}>
        <button type="button" className="link-btn" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show only what is waiting' : `Show everything (${rows.length})`}
        </button>
      </div>

      {shown.length === 0 && (
        <div className="empty">
          <h2>Nothing here</h2>
          <p>
            Requests and subscriptions from the sign-up page land here. The page
            is at <code>/signup</code>.
          </p>
        </div>
      )}

      {shown.map((r) => (
        <section key={r.id} className="review-section signup-row">
          <div className="signup-row-head">
            <h2>
              {r.business_name || r.email}
              {r.status !== 'new' && (
                <span className="chip chip-run-wait">{STATUS_LABEL[r.status] || r.status}</span>
              )}
              {r.client_id && <span className="chip course-chip">already a client</span>}
            </h2>
            <span className="hint">{fmtDateTime(r.created_at)}</span>
          </div>

          <dl className="signup-row-facts">
            <dt>Email</dt><dd><a href={`mailto:${r.email}`}>{r.email}</a></dd>
            {r.website && (
              <>
                <dt>Website</dt>
                <dd>{/^https?:\/\//i.test(r.website)
                  ? <a href={r.website} target="_blank" rel="noreferrer">{r.website}</a>
                  : r.website}</dd>
              </>
            )}
            {r.client_id && (
              <>
                <dt>Client</dt>
                <dd>
                  <Link to={`/admin/clients/${r.client_id}/settings?client=${r.client_id}`}>
                    {r.client_name || r.client_id}
                  </Link>
                </dd>
              </>
            )}
            <dt>Wants</dt>
            <dd>
              {(r.plan_names || []).join(', ') || '—'}
              {' '}<strong>{money(r.total_cents)} {r.bill_interval === 'annual' ? 'a year' : 'a month'}</strong>
            </dd>
            <dt>Switch on</dt>
            <dd>
              {(r.implies_entitlements || []).length === 0 ? '—'
                : (r.implies_entitlements || [])
                    .map((k) => ENTITLEMENT_LABEL[k] || k).join(', ')}
              <span className="hint" style={{ display: 'block', margin: '4px 0 0' }}>
                Set these on the client's settings page. A blog plan also needs its
                cadence set there — this page never changes a live schedule.
              </span>
            </dd>
            {r.notes && (<><dt>They said</dt><dd>{r.notes}</dd></>)}
            {r.agency_note && (<><dt>Your note</dt><dd>{r.agency_note}</dd></>)}
          </dl>

          <label className="field">
            <span className="field-label">Note (optional)</span>
            <input type="text" maxLength={4000} value={notes[r.id] || ''}
                   placeholder="What you agreed, what you charged, anything to remember."
                   onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))} />
          </label>

          <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
            {STATUS.filter(([s]) => s !== r.status).map(([s, label]) => (
              <button key={s} type="button"
                      className={`btn ${s === 'converted' ? 'btn-primary' : ''}`}
                      disabled={busy === r.id}
                      onClick={() => setStatus(r.id, s)}>
                {busy === r.id ? 'Saving…' : label}
              </button>
            ))}
          </div>
        </section>
      ))}

      <p className="hint">
        The prices live in the catalogue, not in the code, so they can change
        without a deploy. Stripe is not wired up yet: when the keys are in,
        this same basket goes to Checkout and the page stops being a form.
      </p>
    </>
  );
}
